import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { LOCALES, messages, type Locale, type Messages } from '@nexops/shared';
import { useUiStore } from '@/shared/theme/ui.store';
import { extraEn, extraPl, type ExtraMessages } from './extra';

type Dict = Messages & ExtraMessages;

/** All dot-separated leaf paths of the dictionary, e.g. `'nav.customers'`. */
type Leaves<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string ? `${P}${K}` : Leaves<T[K], `${P}${K}.`>;
}[keyof T & string];

export type MessageKey = Leaves<Dict>;
export type TranslateVars = Record<string, string | number>;
export type TFunction = (key: MessageKey, vars?: TranslateVars) => string;

type Tree = { [key: string]: string | Tree };

function deepMerge(base: Tree, extra: Tree): Tree {
  const out: Tree = { ...base };
  for (const [k, v] of Object.entries(extra)) {
    const prev = out[k];
    out[k] = typeof v === 'object' && typeof prev === 'object' ? deepMerge(prev, v) : v;
  }
  return out;
}

const DICTIONARIES: Record<Locale, Tree> = {
  pl: deepMerge(messages.pl as unknown as Tree, extraPl as unknown as Tree),
  en: deepMerge(messages.en as unknown as Tree, extraEn as unknown as Tree),
};

function lookup(dict: Tree, key: string): string | undefined {
  let node: string | Tree | undefined = dict;
  for (const part of key.split('.')) {
    if (typeof node !== 'object') return undefined;
    node = node[part];
  }
  return typeof node === 'string' ? node : undefined;
}

function interpolate(template: string, vars?: TranslateVars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m));
}

function translate(locale: Locale, key: MessageKey, vars?: TranslateVars): string {
  const value = lookup(DICTIONARIES[locale], key) ?? lookup(DICTIONARIES.pl, key) ?? key;
  return interpolate(value, vars);
}

/** Translate outside React (toasts from query cache, interceptors). Uses the persisted locale. */
export const tStatic: TFunction = (key, vars) => {
  const locale = useUiStore.getState().locale;
  return translate((LOCALES as readonly string[]).includes(locale) ? locale : 'pl', key, vars);
};

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: TFunction;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const locale = useUiStore((s) => s.locale);
  const setLocale = useUiStore((s) => s.setLocale);
  const safeLocale: Locale = (LOCALES as readonly string[]).includes(locale) ? locale : 'pl';

  useEffect(() => {
    document.documentElement.lang = safeLocale;
  }, [safeLocale]);

  const t = useCallback<TFunction>((key, vars) => translate(safeLocale, key, vars), [safeLocale]);

  const value = useMemo(() => ({ locale: safeLocale, setLocale, t }), [safeLocale, setLocale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

/** Typed translate function: `t('nav.customers')`, `t('common.pageOf', { page: 1, pages: 3 })`. */
export function useT(): TFunction {
  return useI18n().t;
}
